import { postToParent, runSafely, PARENT_EVENTS } from '@/services/parentBridge/parentBridge';
import { ticketTotals } from '@/services/parentBridge/slipSnapshot';
import { resolveErrorDisplay } from '@/services/placeBetErrorDisplay';

/**
 * The outcome of a placement as the parent page sees it (SPORT_BET_PLACED), built
 * from the answer of rocky-bets /cart/v2/place-bet and the slip as it was placed.
 *
 * ticketId is the unique bet code (bet.code), the same ticketId the B2B `tickets`
 * call carries: rocky-bets answers it as bet_code on success and as data on a bet
 * sent to review. shortCode (bet.betCode) is unique only within a platform and is
 * not sent.
 *
 * update_odds is not an outcome: the cart takes the new odds and the player places
 * again (or the cart retries on its own), so no event is built for it.
 *
 * A partner wallet that answers 6503 reaches us as external_save_failed with the
 * reason "Insufficient credit" (rocky-bets gaimClientB2b): insufficientFunds.
 *
 * A bet sent to review (PENDING_REVIEW) is followed on the socket:
 * - PLACE_BET_CHANGED status 'revived': the operator proposes a different bet, a new
 *   ticket whose parentBet is the one in review → SPORT_BET_REVIEW_PROPOSAL;
 * - RESERVE_BET_ACCEPT: the ticket is placed, the one in review accepted by the operator
 *   or the proposal accepted by the player → SPORT_BET_PLACED ACCEPTED;
 * - RESERVE_BET_REJECT: refused by the operator, or the proposal refused by the player
 *   → SPORT_BET_PLACED REJECTED, errorCode REVIEW_REJECTED.
 * Every event of a review carries reviewTicketId, the ticketId sent with PENDING_REVIEW.
 *
 * A rejection carries errorTitle / errorMessage: the text the player reads in the error
 * modal (placeBetErrorDisplay), in the player's language. The translator comes from
 * main.js (setBridgeTranslator): this module cannot import the vue-i18n plugin, which
 * reads the DOM when it loads. Without it both stay null.
 *
 * The notify* functions never throw (runSafely): they are called from cart/placeBet and
 * from the socket handlers, whose flow must not depend on the bridge.
 */
export const BET_STATUS = Object.freeze({
    ACCEPTED:       'ACCEPTED',
    PENDING_REVIEW: 'PENDING_REVIEW',
    REJECTED:       'REJECTED',
});

export const REVIEW_REJECTED = 'REVIEW_REJECTED';

const INSUFFICIENT_CREDIT_REASON = 'Insufficient credit';

const codeOrNull = (value) => (value === undefined || value === null || value === '' ? null : String(value));

const numberOrNull = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

// Tickets in review this page knows, by code: what their final SPORT_BET_PLACED repeats
// (the review they belong to, bet type, stake, potential win). Lost on a reload: the final
// event then carries the code alone.
const REVIEW_MEMORY_MAX = 50;
const reviewTickets = new Map();
// Codes whose final outcome is already posted: the socket can repeat itself, and it can
// resolve a review before the place-bet answer that sends it to review.
const finalizedCodes = new Set();

let translate = null;

/**
 * @param {function|null} fn - (key, values) => text, the app's vue-i18n `t`
 */
export function setBridgeTranslator(fn) {
    translate = typeof fn === 'function' ? fn : null;
}

const NO_ERROR_TEXT = Object.freeze({ errorTitle: null, errorMessage: null });

// The title and the text of the error modal for a rejected placement
function placementErrorText(response) {
    if (!translate) return NO_ERROR_TEXT;
    const shown = resolveErrorDisplay({ $t: translate }, response);
    return { errorTitle: shown.code || null, errorMessage: shown.reason || null };
}

// What the cart shows for a bet refused in review (BetInAcceptanceInCart)
function reviewRejectedText() {
    if (!translate) return NO_ERROR_TEXT;
    return { errorTitle: translate('bet_not_accepted'), errorMessage: translate('betWasNotAccepted') };
}

function remember(collection, key, value) {
    collection.delete(key);
    if (collection instanceof Map) collection.set(key, value);
    else collection.add(key);
    if (collection.size > REVIEW_MEMORY_MAX) collection.delete(collection.keys().next().value);
}

/**
 * @param {object} response - answer of cart/placeBet (success or error body)
 * @param {object} bet - state.cart.bet at placement
 * @returns {object|null} null when the answer is not an outcome (update_odds)
 */
export function buildBetPlacedPayload(response, bet) {
    const res = response || {};
    if (res.error_code === 'update_odds') return null;

    const totals = ticketTotals(bet || {});
    const common = {
        betType:      (bet && bet.type) || null,
        totalStake:   totals.stake,
        potentialWin: totals.potentialWin,
    };

    if (res.result === true) {
        return {
            status: BET_STATUS.ACCEPTED,
            ticketId: codeOrNull(res.bet_code),
            reviewTicketId: null,
            ...common,
            errorCode: null,
            ...NO_ERROR_TEXT,
            insufficientFunds: false,
        };
    }
    if (res.error_code === 'review') {
        const ticketId = typeof res.data === 'string' && res.data !== '' ? res.data : null;
        return {
            status: BET_STATUS.PENDING_REVIEW,
            ticketId,
            reviewTicketId: ticketId,
            ...common,
            errorCode: null,
            ...NO_ERROR_TEXT,
            insufficientFunds: false,
        };
    }
    return {
        status: BET_STATUS.REJECTED,
        ticketId: null,
        reviewTicketId: null,
        ...common,
        errorCode: res.error_code || null,
        ...placementErrorText(res),
        insufficientFunds: res.error_code === 'external_save_failed' && res.reason === INSUFFICIENT_CREDIT_REASON,
    };
}

/**
 * Posts SPORT_BET_PLACED for an answer of cart/placeBet, when it is an outcome.
 *
 * @param {object} response
 * @param {object} bet - state.cart.bet at placement
 * @param {function} [post] - postToParent (injected by the tests)
 */
export function notifyBetPlaced(response, bet, post = postToParent) {
    runSafely('SPORT_BET_PLACED', () => {
        const payload = buildBetPlacedPayload(response, bet);
        if (!payload) return;
        if (payload.status === BET_STATUS.PENDING_REVIEW && payload.ticketId) {
            // Already resolved on the socket: a late PENDING_REVIEW would undo it
            if (finalizedCodes.has(payload.ticketId)) return;
            remember(reviewTickets, payload.ticketId, {
                reviewTicketId: payload.ticketId,
                betType:        payload.betType,
                totalStake:     payload.totalStake,
                potentialWin:   payload.potentialWin,
            });
        }
        post(PARENT_EVENTS.BET_PLACED, payload);
    });
}

/**
 * @param {object} bet - PLACE_BET_CHANGED socket payload with status 'revived' (the proposal)
 * @returns {object}
 */
export function buildReviewProposalPayload(bet) {
    const proposal = bet || {};
    const totals = ticketTotals(proposal);
    return {
        reviewTicketId:   codeOrNull(proposal.parentBet),
        proposalTicketId: codeOrNull(proposal.code),
        betType:          proposal.type || null,
        totalStake:       totals.stake !== null ? totals.stake : numberOrNull(proposal.amount_real),
        potentialWin:     totals.potentialWin,
        selections:       (Array.isArray(proposal.odds) ? proposal.odds : []).map(odd => ({
            selectionId: codeOrNull(odd.unique_id != null ? odd.unique_id : odd.outcome_id),
            odds:        numberOrNull(odd.outcome_odd_value),
        })),
    };
}

/**
 * Posts SPORT_BET_REVIEW_PROPOSAL and keeps the proposal, so that its final outcome
 * points back to the ticket in review.
 *
 * @param {object} bet - PLACE_BET_CHANGED socket payload with status 'revived'
 * @param {function} [post]
 */
export function notifyReviewProposal(bet, post = postToParent) {
    runSafely('SPORT_BET_REVIEW_PROPOSAL', () => {
        const payload = buildReviewProposalPayload(bet);
        if (payload.proposalTicketId) {
            if (payload.reviewTicketId) reviewTickets.delete(payload.reviewTicketId);
            remember(reviewTickets, payload.proposalTicketId, {
                reviewTicketId: payload.reviewTicketId,
                betType:        payload.betType,
                totalStake:     payload.totalStake,
                potentialWin:   payload.potentialWin,
            });
        }
        post(PARENT_EVENTS.BET_REVIEW_PROPOSAL, payload);
    });
}

/**
 * @param {string} status - BET_STATUS.ACCEPTED or BET_STATUS.REJECTED
 * @param {string} code - code of the ticket on the socket (RESERVE_BET_ACCEPT / RESERVE_BET_REJECT)
 * @returns {object}
 */
export function buildReviewOutcomePayload(status, code) {
    const ticketId = codeOrNull(code);
    const known = (ticketId && reviewTickets.get(ticketId)) || {};
    return {
        status,
        ticketId,
        reviewTicketId: known.reviewTicketId || ticketId,
        betType:        known.betType || null,
        totalStake:     known.totalStake !== undefined ? known.totalStake : null,
        potentialWin:   known.potentialWin !== undefined ? known.potentialWin : null,
        errorCode:      status === BET_STATUS.REJECTED ? REVIEW_REJECTED : null,
        ...(status === BET_STATUS.REJECTED ? reviewRejectedText() : NO_ERROR_TEXT),
        insufficientFunds: false,
    };
}

/**
 * Posts the final SPORT_BET_PLACED of a ticket that went through review, once per code.
 *
 * @param {string} status - BET_STATUS.ACCEPTED or BET_STATUS.REJECTED
 * @param {string} code
 * @param {function} [post]
 */
export function notifyReviewOutcome(status, code, post = postToParent) {
    runSafely('SPORT_BET_PLACED (review)', () => {
        const payload = buildReviewOutcomePayload(status, code);
        if (!payload.ticketId || finalizedCodes.has(payload.ticketId)) return;
        remember(finalizedCodes, payload.ticketId);
        reviewTickets.delete(payload.ticketId);
        post(PARENT_EVENTS.BET_PLACED, payload);
    });
}
